import React, { useState, useMemo } from 'react';
import { Box, MuiBox, Button, FlexBox, Grid, InputField, Typography, SelectDropdown } from '@kis-technologies/kis-ui';
import { CreateClientIntegrationPayload } from 'store/clientIntegrations';
import { getCIInitialForm, clientIntegrationValidationSchema, getHasEmptyInput, getHasInvalidInput } from '../../utils';
import { useStyles } from '../../styled';
import { INVALID, VALID } from '../../constants'; // needs to be changed
import { ConfigCreateFormProps } from '../../types';
import { actions as integrationsActions, selectors as integrationsSelectors, Integration } from 'store/integrations';
import { getSortedList } from 'utils/stringFormat';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';

export const CreateClientIntegrationForm = (props: ConfigCreateFormProps) => {
  const { loading, error, resetError, onSubmit } = props;
  const classes = useStyles()();

  // FOR Integrations
  const [getIntegrations] = useActions([integrationsActions.getIntegrations]);
  const integrations = useSelector(integrationsSelectors.selectIntegrations);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration>(integrations[0]);

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getCIInitialForm();
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyInput(form), [form]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  React.useEffect(() => {
    getIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FORM SUBMIT FOR CREATION
  const onCreateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const payload: CreateClientIntegrationPayload = {
      name: form.name,
      baseUrl: form.baseUrl,
      integrationId: form.integrationId
    };
    onSubmit(payload);
  };

  const onInputChange = (key: keyof ReturnType<typeof getCIInitialForm>, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    resetError();
    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getCIInitialForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });

    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await clientIntegrationValidationSchema
        .validate({ [key]: value && value.toString().trim() })
        .catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getCIInitialForm>) => {
    return validationMessage[key];
  };

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  // for integration slection
  const onIntegrationChange = (item: Integration) => {
    setSelectedIntegration(item);
    setForm({ ...form, integrationId: item.id });
  };

  return (
    <FlexBox justify="center" flexOverride="1 1 0%" padding="0 0 11">
      <FlexBox direction="column" fullWidth align="center" justify="center">
        <Grid className={classes.createClientInnerContainer}>
          <Box flexOverride="none" fullWidth margin="0 0 4">
            <Typography className={classes.createNewClientHeading}>Create New Client Integration</Typography>
          </Box>

          <MuiBox className={classes.createClientName}>
            <InputField
              label="name"
              size="medium"
              fullWidth
              placeholder="name"
              value={form.name}
              onChange={(e) => onInputChange('name', e)}
              onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
              helperText={validate('name')}
              error={validation.name === INVALID}
            />
          </MuiBox>
          <MuiBox className={classes.createClientName}>
            <InputField
              label="baseUrl"
              size="medium"
              fullWidth
              placeholder="http://example.com"
              value={form.baseUrl}
              onChange={(e) => onInputChange('baseUrl', e)}
              onBlur={(e) => onInputBlur('baseUrl', e, e.target.value.trim())}
              helperText={validate('baseUrl')}
              error={validation.baseUrl === INVALID}
            />
          </MuiBox>
          <FlexBox fullWidth direction="row" margin="3 0 4" align="center" justifyContent="space-between">
            <Typography fontSize={16} fontWeight={600} sx={{ padding: '0 0 0 24px' }}>
              Integration
            </Typography>
            <SelectDropdown
              placeholder="Select an Integration"
              items={getSortedList(integrations, 'name')}
              selectedItem={selectedIntegration}
              onChange={(item: Integration) => onIntegrationChange(item)}
              itemToString={(item: Integration) => (item ? item.name : '')}
            />
          </FlexBox>
        </Grid>

        {error && <Typography className={classes.createClientErrorText}>{error}</Typography>}

        {/* FORM SUBMIT */}
        <MuiBox className={classes.createClientButtonBox}>
          <Button
            disabled={buttonDisabled}
            customSize="large"
            onClick={onCreateClick}
            isLoading={loading}
            width={334}
            type="submit"
            className={classes.createClientButton}
          >
            Create Client Integration
          </Button>
        </MuiBox>
      </FlexBox>
    </FlexBox>
  );
};
